/**
 * Phase 3.26 — Extracted from DealerSettings.jsx to keep the parent file
 * maintainable. Renders AGB / Datenschutz / Spielregeln acceptance rows.
 * Used standalone or embedded inside BusinessDataSection (Unternehmensdaten).
 */
import { useState } from "react";
import { ExternalLink, CheckCircle2, FileSignature, Globe } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";

const DEFAULT_URLS = {
    agb: "https://twowheels.info/agb",
    datenschutz: "https://twowheels.info/datenschutz",
    spielregeln: "https://twowheels.info/spielregeln",
};

export function LegalSection({ currentUser, embedded = false }) {
    const { refresh } = useAuth();
    const [busy, setBusy] = useState(false);

    const accept = async (which) => {
        setBusy(true);
        try {
            await api.post("/users/me/accept-terms", { [which]: true });
            await refresh();
            toast.success(`${which === "agb" ? "AGB" : which === "datenschutz" ? "Datenschutz" : "Spielregeln"} akzeptiert`);
        } catch {
            toast.error("Speichern fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    const row = (key, label) => {
        const acceptedAt = currentUser[`${key}_accepted_at`];
        return (
            <div className="flex items-center justify-between gap-3 py-3 border-b border-gray-100 last:border-0" data-testid={`legal-${key}`}>
                <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm flex items-center gap-2">
                        <FileSignature className="w-4 h-4 text-gray-400" /> {label}
                        {acceptedAt && <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-[10px]">akzeptiert {acceptedAt.slice(0, 10)}</Badge>}
                    </div>
                    <a href={DEFAULT_URLS[key]} target="_blank" rel="noreferrer" className="text-xs text-[#2563EB] hover:underline flex items-center gap-1 mt-0.5" data-testid={`legal-${key}-link`}>
                        Dokument lesen <ExternalLink className="w-3 h-3" />
                    </a>
                </div>
                {acceptedAt ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                    <Button onClick={() => accept(key)} disabled={busy} className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider text-xs h-8" data-testid={`accept-${key}-btn`}>
                        Akzeptieren
                    </Button>
                )}
            </div>
        );
    };

    const body = (
        <>
            <p className="text-xs text-gray-600 mb-3">Bitte lesen und akzeptieren Sie die folgenden Dokumente.</p>
            {row("agb", "Allgemeine Geschäftsbedingungen (AGB)")}
            {row("datenschutz", "Datenschutzerklärung")}
            {row("spielregeln", "Spielregeln & Verhaltenscodex")}
        </>
    );
    if (embedded) {
        return <div data-testid="legal-section">{body}</div>;
    }
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="legal-section">
            <div className="flex items-center gap-2 mb-1">
                <Globe className="w-5 h-5 text-gray-500" />
                <h3 className="font-display font-bold text-lg">Rechtliches</h3>
            </div>
            {body}
        </div>
    );
}
